<script setup lang="ts">
  import { computed } from 'vue'
  import { ClockIcon, CurrencyDollarIcon } from '@heroicons/vue/24/outline'
  import GeminiComparisonTable from './GeminiComparisonTable.vue'
  import GeminiTranscript from './GeminiTranscript.vue'

  interface GeminiShadowComparison {
    id: number
    created_at: string
    model: string
    latency_ms: number
    input_tokens: number
    output_tokens: number
    total_tokens: number
    input_cost_usd: string
    output_cost_usd: string
    total_cost_usd: string
    gpt_first_name: string | null
    gpt_last_name: string | null
    gpt_email: string | null
    gpt_phone: string | null
    gemini_first_name: {
      value: string | null
      timestamp: string | null
      end_timestamp: string | null
    }
    gemini_last_name: {
      value: string | null
      timestamp: string | null
      end_timestamp: string | null
    }
    gemini_email: { value: string | null; timestamp: string | null; end_timestamp: string | null }
    gemini_phone: { value: string | null; timestamp: string | null; end_timestamp: string | null }
    match_first_name: boolean
    match_last_name: boolean
    match_email: boolean
    match_phone: boolean
    transcription: Array<{ timestamp: string; speaker: 'bot' | 'caller'; text: string }>
    match_count: number
    match_rate: number
    all_match: boolean
  }

  interface GptRecordingTimestamps {
    first_name_recording_start_seconds: string | null
    first_name_recording_end_seconds: string | null
    last_name_recording_start_seconds: string | null
    last_name_recording_end_seconds: string | null
    email_recording_start_seconds: string | null
    email_recording_end_seconds: string | null
    phone_number_recording_start_seconds: string | null
    phone_number_recording_end_seconds: string | null
  }

  const props = defineProps<{
    comparison: GeminiShadowComparison | null
    audioUrl: string | null
    audioElement: HTMLAudioElement | null
    gptTimestamps: GptRecordingTimestamps | null
  }>()

  const emit = defineEmits<{
    (e: 'seek', time: number): void
  }>()

  const handleSeek = (time: number) => {
    emit('seek', time)
  }

  const matchBadgeClass = computed(() => {
    if (!props.comparison) return ''
    if (props.comparison.all_match) return 'bg-green-100 text-green-800'
    if (props.comparison.match_count >= 3) return 'bg-yellow-100 text-yellow-800'
    return 'bg-red-100 text-red-800'
  })

  const matchBadgeText = computed(() => {
    if (!props.comparison) return ''
    return `${props.comparison.match_count}/4 ${props.comparison.all_match ? '✓' : ''}`
  })

  const formatCost = (cost: string | number) => {
    const numCost = typeof cost === 'string' ? parseFloat(cost) : cost
    return `$${numCost.toFixed(4)}`
  }

  const formatLatency = (ms: number) => {
    if (ms < 1000) return `${ms}ms`
    return `${(ms / 1000).toFixed(2)}s`
  }
</script>

<template>
  <div class="space-y-5">
    <!-- No Data State -->
    <div
      v-if="!comparison"
      class="rounded-xl border border-gray-200 bg-white p-8 text-center"
    >
      <div class="text-gray-400">
        <svg
          class="mx-auto h-12 w-12"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
          />
        </svg>
        <h3 class="mt-2 text-sm font-medium text-gray-900">No Gemini Shadow Data</h3>
        <p class="mt-1 text-sm text-gray-500">This call was not processed by Gemini shadow mode.</p>
      </div>
    </div>

    <!-- Gemini Shadow Data -->
    <template v-else>
      <!-- Summary Card -->
      <div class="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div class="flex items-center justify-between">
          <h2 class="text-lg font-semibold text-gray-800">Gemini Shadow Comparison</h2>
          <span
            :class="[
              'inline-flex items-center rounded-full px-3 py-1 text-sm font-medium',
              matchBadgeClass
            ]"
          >
            {{ matchBadgeText }}
          </span>
        </div>

        <!-- Metrics Grid -->
        <div class="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <!-- Model -->
          <div class="rounded-lg bg-gray-50 p-3">
            <div class="text-xs text-gray-500">Model</div>
            <div class="mt-1 text-sm font-medium text-gray-900">{{ comparison.model }}</div>
          </div>

          <!-- Latency -->
          <div class="rounded-lg bg-gray-50 p-3">
            <div class="flex items-center gap-1 text-xs text-gray-500">
              <ClockIcon class="h-3 w-3" />
              Latency
            </div>
            <div class="mt-1 text-sm font-medium text-gray-900">
              {{ formatLatency(comparison.latency_ms) }}
            </div>
          </div>

          <!-- Tokens -->
          <div class="rounded-lg bg-gray-50 p-3">
            <div class="text-xs text-gray-500">Tokens</div>
            <div class="mt-1 text-sm font-medium text-gray-900">
              {{ comparison.total_tokens.toLocaleString() }}
            </div>
            <div class="text-xs text-gray-400">
              {{ comparison.input_tokens.toLocaleString() }} in /
              {{ comparison.output_tokens.toLocaleString() }} out
            </div>
          </div>

          <!-- Cost -->
          <div class="rounded-lg bg-gray-50 p-3">
            <div class="flex items-center gap-1 text-xs text-gray-500">
              <CurrencyDollarIcon class="h-3 w-3" />
              Cost
            </div>
            <div class="mt-1 text-sm font-medium text-gray-900">
              {{ formatCost(comparison.total_cost_usd) }}
            </div>
          </div>
        </div>
      </div>

      <!-- Comparison Table -->
      <GeminiComparisonTable
        :comparison="comparison"
        :audio-url="audioUrl"
        :gpt-timestamps="gptTimestamps"
      />

      <!-- Gemini Transcript -->
      <GeminiTranscript
        :transcription="comparison.transcription"
        :audio-element="audioElement"
        @seek="handleSeek"
      />
    </template>
  </div>
</template>
